import React from 'react';
import formatDate from 'date-fns/format';
import {Platform, Text, View} from 'react-native';
import {images} from '../../../../assets/icons';
import {useIsDesktop} from '../../../../shared/hooks/useIsDesktop';
import {LoadableContent} from '../../../../shared/components/loadableContent';
import {Table} from '../../../../shared/components/table';
import {Column} from '../../../../shared/components/table/model';
import {TransactionItem} from '../../../../shared/models/api/eCertificatesResponsePayload';
import {useTheme} from '../../../../shared/theme';
import {numberFormatToRub} from '../../../../shared/utils/numberFormatToRub';
import {isAmountChangeNegative} from '../../helpers';
import {Collapse} from './Collapse';
import {TruItem} from './TruItem';
import {useHistoryTabStyles} from './HistoryTab.style';

type GroupedTransactions = [string, {amountChange: number; items: TransactionItem[]}][];

interface HistoryTabProps {
  transactions?: GroupedTransactions;
}

export function HistoryTab({transactions}: HistoryTabProps) {
  const styles = useHistoryTabStyles();
  const isDesktop = useIsDesktop();
  const theme = useTheme();

  const columns: Column<TransactionItem>[] = [
    {
      title: 'Наименование',
      field: 'truName',
      sortable: true,
      render: (row) => <Text style={styles.colText}>{row.truName}</Text>,
      headStyles: {
        colHeadStyle: [styles.colHeadStyle, {width: '30%'}],
      },
      styles: {
        colBodyStyle: [styles.colBodyStyle, {width: '30%', justifyContent: 'flex-start'}],
      },
    },
    {
      title: 'Продавец',
      field: 'sellerDepartment',
      sortable: true,
      headStyles: {
        colHeadStyle: [styles.colHeadStyle, {flexBasis: '22%'}],
      },
      styles: {
        colBodyTextStyle: styles.colText,
        colBodyStyle: [styles.colBodyStyle, {flexBasis: '22%'}],
      },
    },
    {
      title: 'Кол-во (шт)',
      field: 'quantity',
      sortable: true,
      headStyles: {
        colHeadStyle: [styles.colHeadStyle, {flexBasis: '12%'}],
      },
      styles: {
        colBodyTextStyle: styles.colText,
        colBodyStyle: [styles.colBodyStyle, {flexBasis: '12%'}],
      },
    },
    {
      title: 'Дата и время',
      field: 'dateTime',
      sortable: true,
      render: (row) => {
        return <Text style={styles.colText}>{formatDate(new Date(row.dateTime), 'dd.MM.yyyy, HH:mm:ss')}</Text>;
      },
      headStyles: {
        colHeadStyle: [styles.colHeadStyle, {flexBasis: '19%'}],
      },
      styles: {
        colBodyStyle: [styles.colBodyStyle, {flexBasis: '19%', justifyContent: 'flex-start'}],
      },
    },
    {
      title: (
        <Text>
          Стоимость,{' '}
          <Text
            style={{
              fontFamily: Platform.select({
                web: 'system-ui',
                ios: 'System',
                android: 'sans-serif',
                default: undefined,
              }),
            }}>
            ₽
          </Text>
        </Text>
      ),
      field: 'amount',
      sortable: true,
      render: (row) => {
        return (
          <Text style={styles.colText}>
            {isAmountChangeNegative(row.typeName) ? '-' : '+'}
            {numberFormatToRub(row.amount * row.quantity, {currencySymbol: 'none'})}
          </Text>
        );
      },
      headStyles: {
        colHeadStyle: [styles.colHeadStyle, {flexBasis: '15%', justifyContent: 'flex-end'}],
      },
      styles: {
        colBodyStyle: [styles.colBodyStyle, {flexBasis: '15%', justifyContent: 'flex-end'}],
      },
    },
  ];

  return (
    <LoadableContent loading={!transactions} style={{marginBottom: Platform.OS !== 'web' ? 50 : 0}}>
      {transactions?.map(([date, {amountChange, items}]) => {
        return (
          <Collapse
            key={date}
            style={{marginBottom: 16}}
            header={
              <View style={styles.historyHeader}>
                {isDesktop && (
                  <>
                    <View style={styles.icon}>
                      <images.Basket width={30} height={30} />
                    </View>
                    <Text style={styles.text}>Изменение баланса</Text>
                  </>
                )}
                <Text style={[styles.text, {minWidth: '20%', marginLeft: isDesktop ? 'auto' : undefined}]}>
                  {formatDate(new Date(date), 'dd.MM.yyyy')}
                </Text>

                <Text
                  style={[
                    styles.text,
                    {
                      minWidth: '15%',
                      textAlign: 'right',
                      color: amountChange > 0 ? '#308953' : theme.color.primaryColor,
                    },
                  ]}>
                  {numberFormatToRub(amountChange, {
                    currencySymbol: '₽',
                    showPositiveSymbol: true,
                    showNegativeSymbol: true,
                  })}
                </Text>
              </View>
            }>
            {items.length === 0 && (
              <View style={{alignItems: 'center', justifyContent: 'center', minHeight: 100}}>
                <Text style={styles.emptyText}>Информация о транзакциях появится позже</Text>
              </View>
            )}

            {isDesktop ? (
              <Table data={items} columns={columns} hideHeaderOnEmpty />
            ) : (
              items.map((item, index, array) => {
                const isLastItem = index === array.length - 1;
                return (
                  <TruItem
                    key={item.truName + index}
                    truName={item.truName}
                    typeName={item.typeName}
                    address={item.address}
                    quantity={item.quantity}
                    amount={item.amount}
                    dateTime={item.dateTime}
                    sellerDepartment={item.sellerDepartment}
                    showBorder={!isLastItem}
                  />
                );
              })
            )}
          </Collapse>
        );
      })}
    </LoadableContent>
  );
}
